import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { type MessageKey, useI18n } from '../i18n';
import { Link, useTitle } from '../router';
import { useAuth } from '../lib/auth';
import { useProgress } from '../lib/progress';
import { LAB_EXPERIMENTS, LAB_TOPICS } from '../lib/labExperiments';
const NEXP = LAB_EXPERIMENTS.length; const NTOP = LAB_TOPICS.length;

type L3 = 'en' | 'ro' | 'tr';
const T = (en: string, ro: string, tr: string) => ({ en, ro, tr });

const TX = {
  title: T('Your Interactive Chemistry Lab', 'Laboratorul tău interactiv de chimie', 'Etkileşimli Kimya Laboratuvarın'),
  sub: T('Explore. Experiment. Understand.', 'Explorează. Experimentează. Înțelege.', 'Keşfet. Deney yap. Anla.'),
  lead: T('Real reactions, a patient AI teacher and questions that explain your mistakes — in English, Romanian and Turkish.',
    'Reacții reale, un profesor AI răbdător și întrebări care îți explică greșelile — în engleză, română și turcă.',
    'Gerçek tepkimeler, sabırlı bir yapay zekâ öğretmen ve hatalarını açıklayan sorular — Türkçe, İngilizce ve Rumence.'),
  exp: T('Start an Experiment', 'Începe un experiment', 'Deneye başla'),
  expD: T(`${NEXP} guided experiments: mix, heat, test gases and watch reactions.`, `${NEXP} de experimente ghidate: amestecă, încălzește, testează gaze.`, `${NEXP} rehberli deney: karıştır, ısıt, gazları test et, tepkimeleri izle.`),
  ask: T('Ask Prof. AKAY', 'Întreabă-l pe Prof. AKAY', 'Prof. AKAY’a sor'),
  askD: T('Your AI chemistry teacher explains step by step — and reads aloud.', 'Profesorul tău AI explică pas cu pas — și citește cu voce tare.', 'Yapay zekâ kimya öğretmenin adım adım anlatır, sesli okur.'),
  quiz: T('Challenge Yourself', 'Pune-te la încercare', 'Kendini dene'),
  quizD: T('5 to 50 questions, with feedback on every mistake.', 'Între 5 și 50 de întrebări, cu explicații la fiecare greșeală.', '5 ile 50 arası soru; her hatanın açıklamasıyla.'),
  go: T('Start', 'Începe', 'Başla'),
  hello: T('Welcome back', 'Bine ai revenit', 'Tekrar hoş geldin'),
  cont: T('Continue where you left off', 'Continuă de unde ai rămas', 'Kaldığın yerden devam et'),
  contBtn: T('Continue', 'Continuă', 'Devam et'),
  recent: T('Recent activity', 'Activitate recentă', 'Son etkinlikler'),
  topics: T('Quiz results by topic', 'Rezultate pe teme', 'Konulara göre test sonuçları'),
  labDone: T('lab experiments completed', 'experimente finalizate', 'deney tamamlandı'),
  quizzes: T('quizzes', 'teste', 'test'),
  saved: T('saved results', 'rezultate salvate', 'kayıtlı sonuç'),
  lastQuiz: T('Last quiz', 'Ultimul test', 'Son test'),
  allProgress: T('All progress', 'Tot progresul', 'Tüm ilerleme'),
  about: T('ChemAI is a free interactive chemistry platform for students and teachers: a virtual lab, practice questions, an AI tutor and Google-Classroom-style classes. Teachers can optionally connect Google Classroom to import their classes and send ChemAI grades back.',
    'ChemAI este o platformă interactivă gratuită de chimie pentru elevi și profesori: laborator virtual, exerciții, tutore AI și clase în stil Google Classroom. Profesorii pot conecta opțional Google Classroom pentru a importa clasele și a trimite notele din ChemAI.',
    'ChemAI, öğrenciler ve öğretmenler için ücretsiz, etkileşimli bir kimya platformudur: sanal laboratuvar, alıştırma soruları, yapay zekâ öğretmeni ve Google Classroom tarzı sınıflar. Öğretmenler isterse Google Classroom’u bağlayarak sınıflarını içe aktarabilir ve ChemAI notlarını geri gönderebilir.'),
  privacy: T('Privacy policy', 'Politica de confidențialitate', 'Gizlilik politikası'),
  schools: T('For schools', 'Pentru școli', 'Okullar için'),
  device: T('Progress is saved on this device.', 'Progresul este salvat pe acest dispozitiv.', 'İlerleme bu cihazda kaydedilir.'),
  modules: T('Everything in ChemAI', 'Tot ce oferă ChemAI', 'ChemAI’de neler var'),
  newTag: T('New', 'Nou', 'Yeni'),
  chemlab: T('Chemistry lab', 'Laborator de chimie', 'Kimya laboratuvarı'),
  games: T('Chemistry games', 'Jocuri de chimie', 'Kimya oyunları'),
  library: T('Library', 'Bibliotecă', 'Kitaplık'),
  exam: T('Exam practice', 'Pregătire pentru examen', 'Sınava hazırlık'),
  gamesD: T('12 games from oxides to pH, live duels and class contests.', '12 jocuri, de la oxizi la pH, dueluri live și concursuri pe clase.', 'Oksitlerden pH’a 12 oyun, canlı düellolar ve sınıf yarışmaları.'),
};

const TOPIC_NAMES: Record<string, { en: string; ro: string; tr: string }> = {
  'atomic-structure': T('Atomic structure', 'Structura atomului', 'Atomun yapısı'),
  bonding: T('Bonding', 'Legături chimice', 'Kimyasal bağlar'),
  inorganic: T('Inorganic', 'Chimie anorganică', 'Anorganik'),
  'organic-nomenclature': T('Organic naming', 'Nomenclatură organică', 'Organik adlandırma'),
  stoichiometry: T('Stoichiometry', 'Stoechiometrie', 'Stokiyometri'),
  solutions: T('Solutions', 'Soluții', 'Çözeltiler'),
  'acids-bases': T('Acids & bases', 'Acizi și baze', 'Asitler ve bazlar'),
};

const MODULES: Array<{ key: string; to: string; nav?: MessageKey; glyph: string; d: { en: string; ro: string; tr: string }; external?: boolean; isNew?: boolean }> = [
  { key: 'chemlab', to: '/chemlab/', glyph: '🧪', external: true, isNew: true, d: T(`${NEXP} experiments in ${NTOP} topics`, `${NEXP} de experimente în ${NTOP} teme`, `${NTOP} konuda ${NEXP} deney`) },
  { key: 'games', to: '/games/', glyph: '🎮', external: true, isNew: true, d: T('12 games, duels and class contests', '12 jocuri, dueluri și concursuri', '12 oyun, düello ve sınıf yarışmaları') },
  { key: 'learn', to: '/learn/', glyph: '⚛️', external: true, isNew: true, d: T('18 animated lessons for every topic', '18 lecții animate pentru fiecare temă', 'Her konu için 18 animasyonlu ders') },
  { key: 'library', to: '/library/', glyph: '📚', external: true, isNew: true, d: T('Free books, courses and PDF notes', 'Cărți, cursuri și note PDF gratuite', 'Ücretsiz kitaplar, dersler ve PDF notlar') },
  { key: 'exam', to: '/exam', glyph: '🎯', isNew: true, d: T('Cambridge IGCSE and IB-style timed tests', 'Teste cronometrate stil Bacalaureat, IGCSE și IB', 'TYT/AYT, IGCSE ve IB tarzı süreli denemeler') },
  { key: 'periodic', to: '/periodic-table', nav: 'nav.periodic', glyph: 'Fe', d: T('All 118 elements and trends', 'Toate cele 118 elemente', '118 element ve periyodik özellikler') },
  { key: 'quiz', to: '/quiz', nav: 'nav.quiz', glyph: '✓', d: T('Practise with instant feedback', 'Exersează cu feedback imediat', 'Anında geri bildirimle çalış') },
  { key: 'lab', to: '/lab/titration', nav: 'nav.lab', glyph: 'pH', d: T('Titration, dilution, gas laws', 'Titrare, diluare, legile gazelor', 'Titrasyon, seyreltme, gaz yasaları') },
  { key: 'tutor', to: '/tutor', nav: 'nav.tutor', glyph: 'PA', d: T('Ask anything, or take a lesson', 'Întreabă orice sau ia o lecție', 'Soru sor ya da ders al') },
  { key: 'molecules', to: '/molecules', nav: 'nav.molecules', glyph: 'H₂O', d: T('Draw and rotate 3-D molecules', 'Desenează molecule 3D', '3B molekül çiz ve döndür') },
  { key: 'simulations', to: '/simulations', nav: 'nav.simulations', glyph: '▶', d: T('Interactive simulations', 'Simulări interactive', 'Etkileşimli simülasyonlar') },
  { key: 'projects', to: '/projects', nav: 'nav.projects', glyph: '✎', d: T('Hands-on science projects', 'Proiecte practice', 'Uygulamalı bilim projeleri') },
  { key: 'competitions', to: '/competitions', nav: 'nav.competitions', glyph: '★', d: T('Olympiads and science fairs', 'Olimpiade și concursuri', 'Olimpiyatlar ve bilim fuarları') },
  { key: 'dashboard', to: '/dashboard', nav: 'nav.dashboard', glyph: '%', d: T('Your scores and mastery', 'Scorurile și nivelul tău', 'Puanların ve ustalığın') },
  { key: 'student', to: '/student', nav: 'nav.student', glyph: '◎', d: T('Join a class', 'Intră într-o clasă', 'Bir sınıfa katıl') },
  { key: 'teacher', to: '/teacher', nav: 'nav.teacher', glyph: '▤', d: T('Build quizzes, run classes', 'Creează teste, gestionează clase', 'Test hazırla, sınıf yönet') },
];

/** Written by the standalone lab at /chemlab/ (same origin, same localStorage). */
interface LabMemory { last?: { id: string; t: { en: string; ro: string; tr: string }; at: string }; done?: string[] }
function readLab(): LabMemory | null {
  try {
    const raw = localStorage.getItem('chemai.chemlab.v1');
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v === 'object' ? (v as LabMemory) : null;
  } catch { return null; }
}

const CSS = `
.hx-hero{position:relative;display:grid;grid-template-columns:minmax(0,1fr) auto;gap:24px;align-items:center;padding:clamp(22px,4vw,44px);border-radius:22px;border:1px solid #e3dccb;
  background:radial-gradient(120% 140% at 100% 0%,rgba(13,112,104,.10),transparent 55%),linear-gradient(#fffdf8,#fffdf8);overflow:hidden}
.hx-hero::before{content:"";position:absolute;inset:0;background-image:linear-gradient(rgba(13,112,104,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(13,112,104,.05) 1px,transparent 1px);background-size:28px 28px;pointer-events:none}
.hx-hero>*{position:relative}
.hx-brand{font-size:13px;letter-spacing:.14em;text-transform:uppercase;color:#0d7068;font-weight:700;margin:0 0 8px}
.hx-title{margin:0;font-size:clamp(30px,4.6vw,52px);line-height:1.05;letter-spacing:-.02em;text-wrap:balance}
.hx-title span{color:#0d7068}
.hx-sub{margin:12px 0 0;font-size:clamp(18px,2.2vw,24px);font-weight:600;color:#3d4b4a}
.hx-sub i{font-style:normal;color:#0d7068}
.hx-lead{margin:12px 0 0;max-width:58ch;color:#5b6766}
.hx-tiles{display:grid;grid-template-columns:repeat(3,72px);grid-template-rows:repeat(3,72px);gap:10px}
.hx-tile{position:relative;border-radius:12px;border:1.5px solid #1d2a2a;background:#fffdf8;display:grid;place-items:center;font:700 30px/1 Georgia,'Times New Roman',serif;animation:hx-float 5s ease-in-out infinite}
.hx-tile small{position:absolute;top:6px;left:8px;font:600 11px/1 ui-monospace,monospace}
.hx-tile.big{grid-column:1/3;grid-row:1/3;background:#0d7068;color:#fff;border-color:#0d7068;font-size:64px}
.hx-tile.o{grid-column:3;grid-row:1}.hx-tile.h{grid-column:3;grid-row:2}.hx-tile.n{grid-column:2;grid-row:3;background:#f6ecd6}
.hx-tile:nth-child(2){animation-delay:-1.2s}.hx-tile:nth-child(3){animation-delay:-2.4s}.hx-tile:nth-child(4){animation-delay:-3.6s}
@keyframes hx-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
.hx-big3{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px;margin:20px 0 8px}
.hx-entry{display:flex;flex-direction:column;gap:10px;padding:22px;border-radius:18px;text-decoration:none;color:#1d2a2a;border:1px solid #e3dccb;background:#fffdf8;transition:transform .15s ease,box-shadow .15s ease,border-color .15s ease}
.hx-entry:hover{transform:translateY(-3px);box-shadow:0 10px 30px rgba(13,112,104,.14);border-color:#0d7068}
.hx-entry.primary{background:#0d7068;color:#fff;border-color:#0d7068}
.hx-entry .ico{width:48px;height:48px;border-radius:14px;display:grid;place-items:center;font-size:24px;background:rgba(13,112,104,.10);color:#0d7068}
.hx-entry.primary .ico{background:rgba(255,255,255,.16);color:#fff}
.hx-entry h3{margin:0;font-size:21px;letter-spacing:-.01em}
.hx-entry p{margin:0;font-size:14.5px;opacity:.85;flex:1}
.hx-go{align-self:flex-start;font-weight:700;font-size:14px;padding:8px 14px;border-radius:10px;background:#0d7068;color:#fff}
.hx-entry.primary .hx-go{background:#fff;color:#0d7068}
.hx-games{display:flex;align-items:center;gap:14px;flex-wrap:wrap;margin:16px 0 0;padding:16px 20px;border-radius:18px;text-decoration:none;color:#fff;background:linear-gradient(100deg,#20314f,#3e5f88);transition:transform .15s ease}
.hx-games:hover{transform:translateY(-2px)}
.hx-games .gi{font-size:30px}
.hx-games .gt{flex:1;min-width:200px}
.hx-games b{display:block;font-size:19px}
.hx-games span.gd{opacity:.85;font-size:14px}
.hx-games .hx-go{background:#ffd36b;color:#20314f;align-self:center}
.hx-sec{margin:34px 0 14px;font-size:22px;display:flex;align-items:baseline;gap:12px;flex-wrap:wrap}
.hx-sec small{font-size:13px;color:#7a8584;font-weight:400}
.hx-cont{display:grid;grid-template-columns:minmax(0,1.2fr) minmax(0,1fr) minmax(0,1fr);gap:16px}
.hx-panel{border:1px solid #e3dccb;background:#fffdf8;border-radius:16px;padding:18px;min-width:0}
.hx-panel h4{margin:0 0 10px;font-size:13px;letter-spacing:.06em;text-transform:uppercase;color:#7a8584}
.hx-resume{display:flex;flex-direction:column;gap:12px;background:linear-gradient(135deg,#0d7068,#0b5c56);color:#fff;border:0}
.hx-resume h4{color:rgba(255,255,255,.75)}
.hx-resume strong{font-size:20px}
.hx-resume a{align-self:flex-start;background:#fff;color:#0d7068;font-weight:700;border-radius:10px;padding:8px 14px;text-decoration:none}
.hx-stats{display:flex;gap:18px;flex-wrap:wrap}
.hx-stats div{display:flex;flex-direction:column}
.hx-stats b{font-size:26px;line-height:1.1}
.hx-stats span{font-size:12.5px;opacity:.8}
.hx-list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px}
.hx-list a{display:flex;justify-content:space-between;gap:10px;text-decoration:none;color:inherit;padding:6px 0;border-bottom:1px dashed #e3dccb}
.hx-list a:hover{color:#0d7068}
.hx-list time{font-size:12.5px;color:#7a8584;white-space:nowrap}
.hx-bar{display:grid;grid-template-columns:minmax(0,1fr) 44px;gap:4px 10px;align-items:center;font-size:13.5px;margin-bottom:8px}
.hx-bar i{grid-column:1/-1;height:7px;border-radius:99px;background:#efe8d8;overflow:hidden;display:block}
.hx-bar i::after{content:"";display:block;height:100%;width:var(--w);background:#0d7068;border-radius:inherit}
.hx-bar em{font-style:normal;text-align:right;font-variant-numeric:tabular-nums;color:#0d7068;font-weight:600}
.hx-muted{color:#7a8584;font-size:13px;margin:10px 0 0}
.hx-foot{margin-top:36px;padding-top:16px;border-top:1px solid var(--line, rgba(0,0,0,.08));color:var(--muted, #5d675f);font-size:13px;line-height:1.55;max-width:70ch}
.hx-foot p{margin:0 0 6px}
.hx-foot a{color:inherit;text-decoration:underline}
.hx-mods{list-style:none;margin:0;padding:0;display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px}
.hx-mods li{display:flex}
.hx-mod{flex:1;display:grid;grid-template-columns:44px minmax(0,1fr) auto;gap:2px 12px;align-items:center;padding:14px;border:1px solid #e3dccb;border-radius:14px;background:#fffdf8;text-decoration:none;color:#1d2a2a;transition:border-color .15s ease,transform .15s ease}
.hx-mod:hover{border-color:#0d7068;transform:translateY(-2px)}
.hx-mod .g{grid-row:span 2;width:44px;height:44px;border-radius:12px;display:grid;place-items:center;background:rgba(13,112,104,.10);color:#0d7068;font:700 15px/1 Georgia,serif}
.hx-mod .tt{font-weight:700;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.hx-mod .tt em{font-style:normal;font-size:11px;font-weight:700;background:#f4a93b;color:#2a1a00;border-radius:6px;padding:1px 6px}
.hx-mod .dd{grid-column:2;font-size:13.5px;color:#5b6766}
.hx-mod .ar{grid-column:3;grid-row:1/3;width:32px;height:32px;border-radius:50%;display:grid;place-items:center;background:rgba(13,112,104,.10);color:#0d7068;font-weight:700;transition:background .15s ease,color .15s ease}
.hx-mod:hover .ar{background:#0d7068;color:#fff}
@media (max-width:900px){.hx-big3{grid-template-columns:minmax(0,1fr)}.hx-cont{grid-template-columns:minmax(0,1fr)}}
@media (max-width:640px){.hx-hero{grid-template-columns:minmax(0,1fr)}.hx-tiles{display:none}}
@media (prefers-reduced-motion:reduce){.hx-tile{animation:none}.hx-entry,.hx-mod{transition:none}}
`;

export function HomePage() {
  const { t, lang } = useI18n();
  useTitle(t('nav.home'));
  const L = (o: { en: string; ro: string; tr: string }) => o[(lang as L3)] ?? o.en;
  const { user } = useAuth();
  const prog = useProgress();
  const lab = useMemo(readLab, []);
  const labHref = (hash = '') => `/chemlab/?lang=${lang}${hash ? `#${hash}` : ''}`;
  const date = (iso: string) => { try { return new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short' }); } catch { return ''; } };

  const topicScores = useMemo(() => {
    const acc: Record<string, { s: number; n: number }> = {};
    for (const q of prog.quizzes) {
      if (!q.topic || !q.total) continue;
      const a = (acc[q.topic] ??= { s: 0, n: 0 });
      a.s += q.score; a.n += q.total;
    }
    return Object.entries(acc).map(([k, v]) => ({ k, p: Math.round((100 * v.s) / v.n) })).sort((a, b) => b.p - a.p).slice(0, 5);
  }, [prog.quizzes]);

  const recent = useMemo(() => {
    const items: Array<{ key: string; label: string; href: string; at: string; external?: boolean }> = [];
    for (const e of prog.experiments.slice(0, 5)) items.push({ key: e.id, label: t(`lab.sim.${e.kind}` as MessageKey), href: `/lab/${e.kind}`, at: e.date });
    if (lab?.last) items.push({ key: 'chemlab', label: `🧪 ${L(lab.last.t)}`, href: labHref(lab.last.id), at: lab.last.at, external: true });
    for (const q of prog.quizzes.slice(0, 3)) items.push({ key: q.id, label: `✓ ${q.topic && TOPIC_NAMES[q.topic] ? L(TOPIC_NAMES[q.topic]) : t('nav.quiz')} · ${q.score}/${q.total}`, href: '/quiz', at: q.date });
    return items.sort((a, b) => b.at.localeCompare(a.at)).slice(0, 5);
  }, [prog, lab, lang]);

  const hasProgress = prog.quizzes.length > 0 || prog.experiments.length > 0 || !!lab?.last;
  const lastQuiz = prog.quizzes[0];
  const resume = lab?.last
    ? { label: L(lab.last.t), href: labHref(lab.last.id), external: true }
    : prog.experiments[0]
      ? { label: t(`lab.sim.${prog.experiments[0].kind}` as MessageKey), href: `/lab/${prog.experiments[0].kind}`, external: false }
      : { label: L(TX.quiz), href: '/quiz', external: false };

  return (
    <div className="page">
      <style>{CSS}</style>

      <section className="hx-hero">
        <div>
          <p className="hx-brand">ChemAI</p>
          <h1 className="hx-title"><span>ChemAI</span> — {L(TX.title)}</h1>
          <p className="hx-sub">{L(TX.sub).split('.').filter((x) => x.trim()).map((w, i) => <span key={i}>{w.trim()}<i>.</i> </span>)}</p>
          <p className="hx-lead">{L(TX.lead)}</p>
          <p style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            {!user && <Link className="btn btn-primary" to="/try">🧪 {lang === 'ro' ? 'Încearcă fără cont' : lang === 'tr' ? 'Hesapsız dene' : 'Try without an account'}</Link>}
            {!user && <Link className="btn" to="/login">{lang === 'ro' ? 'Autentificare / cont nou' : lang === 'tr' ? 'Giriş / hesap aç' : 'Sign in / sign up'}</Link>}
            {user?.role !== 'student' && <a className="btn" href={`/schools/?lang=${lang}`}>🏫 {L(TX.schools)}</a>}
          </p>
        </div>
        <div className="hx-tiles" aria-hidden="true">
          <div className="hx-tile big"><small>6</small>C</div>
          <div className="hx-tile o"><small>8</small>O</div>
          <div className="hx-tile h"><small>1</small>H</div>
          <div className="hx-tile n"><small>7</small>N</div>
        </div>
      </section>

      <div className="hx-big3">
        <a href={labHref()} className="hx-entry primary">
          <span className="ico" aria-hidden="true">🧪</span>
          <h3>{L(TX.exp)}</h3>
          <p>{L(TX.expD)}</p>
          <span className="hx-go">{L(TX.go)} →</span>
        </a>
        <Link to={user ? '/tutor' : '/login?next=/tutor'} className="hx-entry">
          <span className="ico" aria-hidden="true">✦</span>
          <h3>{L(TX.ask)}</h3>
          <p>{L(TX.askD)}</p>
          <span className="hx-go">{L(TX.go)} →</span>
        </Link>
        <Link to="/quiz" className="hx-entry">
          <span className="ico" aria-hidden="true">✓</span>
          <h3>{L(TX.quiz)}</h3>
          <p>{L(TX.quizD)}</p>
          <span className="hx-go">{L(TX.go)} →</span>
        </Link>
      </div>
      <a href={`/games/?lang=${lang}`} className="hx-games">
        <span className="gi" aria-hidden="true">🎮</span>
        <span className="gt"><b>{L(TX.games)}</b><span className="gd">{L(TX.gamesD)}</span></span>
        <span className="hx-go">{L(TX.go)} →</span>
      </a>

      {hasProgress && (
        <>
          <h2 className="hx-sec">{user ? `${L(TX.hello)}, ${user.displayName}` : L(TX.cont)} <small>{L(TX.device)}</small></h2>
          <div className="hx-cont">
            <div className="hx-panel hx-resume">
              <h4>{L(TX.cont)}</h4>
              <strong>{resume.label}</strong>
              {resume.external ? <a href={resume.href}>{L(TX.contBtn)} →</a> : <Link to={resume.href}>{L(TX.contBtn)} →</Link>}
              <div className="hx-stats">
                <div><b>{lab?.done?.length ?? 0}/{NEXP}</b><span>{L(TX.labDone)}</span></div>
                <div><b>{prog.quizzes.length}</b><span>{L(TX.quizzes)}</span></div>
                <div><b>{prog.experiments.length}</b><span>{L(TX.saved)}</span></div>
              </div>
            </div>
            <div className="hx-panel">
              <h4>{L(TX.recent)}</h4>
              <ul className="hx-list">
                {recent.map((r) => (
                  <li key={r.key}>
                    {r.external
                      ? <a href={r.href}><span>{r.label}</span><time dateTime={r.at}>{date(r.at)}</time></a>
                      : <Link to={r.href}><span>{r.label}</span><time dateTime={r.at}>{date(r.at)}</time></Link>}
                  </li>
                ))}
              </ul>
            </div>
            <div className="hx-panel">
              <h4>{L(TX.topics)}</h4>
              {topicScores.length > 0 ? topicScores.map((s) => (
                <div className="hx-bar" key={s.k}>
                  <span>{TOPIC_NAMES[s.k] ? L(TOPIC_NAMES[s.k]) : s.k}</span><em>{s.p}%</em>
                  <i style={{ '--w': `${s.p}%` } as CSSProperties} />
                </div>
              )) : lastQuiz ? (
                <p>{L(TX.lastQuiz)}: <b>{lastQuiz.score}/{lastQuiz.total}</b></p>
              ) : (
                <p className="hx-muted">—</p>
              )}
              <p className="hx-muted"><Link to="/dashboard">{L(TX.allProgress)} →</Link></p>
            </div>
          </div>
        </>
      )}

      <h2 className="hx-sec">{L(TX.modules)}</h2>
      <ul className="hx-mods">
        {MODULES.map((m) => {
          const title = m.nav ? t(m.nav) : m.key === 'games' ? L(TX.games) : m.key === 'library' ? L(TX.library) : m.key === 'exam' ? L(TX.exam) : m.key === 'learn' ? L({ en: 'Interactive lessons', ro: 'Lecții interactive', tr: 'İnteraktif dersler' }) : L(TX.chemlab);
          const body = (
            <>
              <span className="g" aria-hidden="true">{m.glyph}</span>
              <span className="tt">{title}{m.isNew && <em>{L(TX.newTag)}</em>}</span>
              <span className="dd">{L(m.d)}</span>
              <span className="ar" aria-hidden="true">→</span>
            </>
          );
          return (
            <li key={m.key}>
              {m.external ? <a href={m.key === 'chemlab' ? labHref() : `${m.to}?lang=${lang}`} className="hx-mod">{body}</a> : <Link to={m.key === 'tutor' && !user ? '/login?next=/tutor' : m.to} className="hx-mod">{body}</Link>}
            </li>
          );
        })}
      </ul>

      <InstallApp lang={lang} />
      <footer className="hx-foot">
        <p>{L(TX.about)}</p>
        <p><a href={`/schools/?lang=${lang}`}>{L(TX.schools)}</a> · <a href={`/library/?lang=${lang}`}>{L(TX.library)}</a> · <a href={`/privacy/#${lang}`}>{L(TX.privacy)}</a></p>
      </footer>
    </div>
  );
}


/* "Install ChemAI" card: one-tap install where the browser supports it, instructions on iPhone/iPad. */
function InstallApp({ lang }: { lang: string }) {
  const L3 = (en: string, ro: string, tr: string) => (lang === 'ro' ? ro : lang === 'tr' ? tr : en);
  const [can, setCan] = useState<boolean>(() => typeof window !== 'undefined' && !!(window as unknown as { __chemaiInstall?: unknown }).__chemaiInstall);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const on = () => setCan(true);
    window.addEventListener('chemai-installable', on);
    window.addEventListener('appinstalled', () => setDone(true));
    return () => window.removeEventListener('chemai-installable', on);
  }, []);
  const standalone = typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone);
  const ios = typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);
  if (standalone || done || (!can && !ios)) return null;
  const install = async () => {
    const ev = (window as unknown as { __chemaiInstall?: { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> } }).__chemaiInstall;
    if (!ev) return;
    await ev.prompt();
    const r = await ev.userChoice; if (r.outcome === 'accepted') setDone(true);
  };
  return (
    <div className="hx-games" style={{ background: 'linear-gradient(100deg,#0d7068,#1f9b74)', marginTop: 16 }}>
      <span className="gi" aria-hidden="true">📲</span>
      <span className="gt"><b>{L3('Install ChemAI on your phone', 'Instalează ChemAI pe telefon', 'ChemAI’ı telefonuna yükle')}</b>
        <span className="gd">{ios
          ? L3('In Safari tap Share ⬆, then “Add to Home Screen”. The lab and games then work without internet.', 'În Safari apasă Partajare ⬆, apoi „Adaugă pe ecranul principal”. Laboratorul și jocurile merg apoi fără internet.', 'Safari’de Paylaş ⬆ simgesine, sonra “Ana Ekrana Ekle”ye dokun. Laboratuvar ve oyunlar internetsiz de çalışır.')
          : L3('Opens like an app; the lab and games also work offline.', 'Se deschide ca o aplicație; laboratorul și jocurile merg și offline.', 'Uygulama gibi açılır; laboratuvar ve oyunlar internetsiz de çalışır.')}</span></span>
      {!ios && <button type="button" className="hx-go" style={{ border: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700 }} onClick={() => void install()}>{L3('Install', 'Instalează', 'Yükle')}</button>}
    </div>
  );
}
